import { 
  DataSourceItem, 
  IngestionRunItem, 
  DataQuarantineItem, 
  DataQualityRuleItem, 
  FreshnessPolicyItem, 
  ExchangeRateObservationItem, 
  TaxRuleSetItem, 
  DataConflictItem, 
  DataIncidentItem, 
  DataLineageNode, 
  GlobalMarketReadinessItem, 
  DeliverableP3 
} from '../types';

// ============================================================================
// 1. MASTER DELIVERABLES (A -> AT) COMPLETE TECHNICAL SPECIFICATIONS
// ============================================================================

export const DELIVERABLES_P3: DeliverableP3[] = [
  {
    code: 'A',
    titleEn: 'Data Architecture Vision',
    titleFr: 'Vision de l\'Architecture des Données',
    category: 'Strategy & Domains',
    summary: 'The YamooPay Financial Data Trust Layer: "No financial value without provenance". Every calculation is identifiable, versioned, timestamped, traceable, auditable, validated, and reproducible.',
    details: [
      'PostgreSQL 18 as the single System of Record (SoR) for all canonical reference, regulatory, and observational data.',
      'Absolute prohibition of floating-point arithmetic (FLOAT/REAL) for financial computations — strict NUMERIC/DECIMAL with explicit scale and precision.',
      'Bitemporal modeling across all regulatory tax rules: strict distinction between Valid Time (real-world applicability) and System Time (YamooPay ingestion/audit).',
      'Correctness Before Coverage: If a jurisdiction lacks certified, reliable statutory sources, the engine displays "Not Yet Supported" rather than generating approximations.',
      'Light Mode for all diagnostic, admin, and lineage visualization surfaces.'
    ],
    architectureNotes: 'Core principle: System of Record vs Ingestion Pipeline vs Read Models. Zero untracked mutations in production databases.'
  },
  {
    code: 'B',
    titleEn: 'Domain Map & Bounded Contexts',
    titleFr: 'Cartographie des Domaines & Contextes Délimités',
    category: 'Strategy & Domains',
    summary: 'Explicit isolation of 12 distinct database domain schemas with strict foreign key boundaries and module interfaces.',
    details: [
      '1. identity: Tenant, user profiles, authentication metadata, workspace settings.',
      '2. geography: Sovereign states, territories, administrative subdivisions, economic zones, locales.',
      '3. currency: ISO 4217 fiat assets, minor units, precision definitions, historical currencies.',
      '4. exchange: FX observations (mid-market, bid, ask, reference), derived crosses, reciprocal rates.',
      '5. transfer: Remittance providers, corridor availabilities, live quote snapshots, fee structures.',
      '6. tax: Statutory tax years, bracket curves, deductions, credits, bitemporal rulesets.',
      '7. salary: Social contribution curves, employer vs employee payroll levies, exemption thresholds.',
      '8. lending: Reference benchmark rates (SOFR, EURIBOR, SONIA), loan amortizations, fee bounds.',
      '9. content: Methodologies, disclosure revisions, localized tax explainers, SEO freshness bindings.',
      '10. operations: Ingestion pipelines, source health checks, telemetry, quarantine queues, incident records.',
      '11. governance: Four-eyes change requests, authority policies, SLA policies, data contracts.',
      '12. audit: Append-only ledger of statutory rule activations, administrative overrides, data mutations.'
    ],
    architectureNotes: 'Implemented via dedicated PostgreSQL schemas (`CREATE SCHEMA geography;`) to prevent monolithic namespace collisions.'
  },
  {
    code: 'C',
    titleEn: 'PostgreSQL 18 Architecture & System of Record',
    titleFr: 'Architecture PostgreSQL 18 & Source de Vérité',
    category: 'PostgreSQL & Schema',
    summary: 'Production PostgreSQL 18.x configuration tuned for high-integrity financial transactions, deterministic decimal precision, and hostinger VPS Dokploy environments.',
    details: [
      'Hostinger VPS + Dokploy + Docker containerized deployment with pinned `postgres:18.1-alpine` container.',
      'Persistent volume mounts with WAL archiving and continuous base backups to external encrypted object storage.',
      'Connection pooling via PgBouncer (transaction pooling mode) to absorb client bursts and Next.js serverless instances.',
      'Strict character encoding: UTF8, Collation: en_US.UTF-8 / uca-1500 (Unicode Collation Algorithm).',
      'Configuration tuning: shared_buffers = 25% RAM, effective_cache_size = 75% RAM, work_mem = 64MB, maintenance_work_mem = 512MB, wal_level = replica.'
    ],
    architectureNotes: 'Never deploy postgres:latest in production. Explicit upgrade runbooks with staging validation and pre-upgrade physical backups.'
  },
  {
    code: 'D',
    titleEn: 'Schema Strategy & DDL Conventions',
    titleFr: 'Stratégie de Schéma & Conventions DDL',
    category: 'PostgreSQL & Schema',
    summary: 'Uniform database naming conventions (snake_case in SQL, camelCase in TypeScript ORM) with mandatory database-level invariants.',
    details: [
      'Primary Keys: UUIDv7 (`uuid_generate_v7()` / native pg18) for time-ordered locality, high index insertion speed, and non-enumerable security.',
      'Money Value Pattern: Composite storage of `amount NUMERIC(18, 4)` + `currency_id UUID` + `minor_unit INT`.',
      'Database Constraints: Mandatory NOT NULL, CHECK bounds (`CHECK (rate > 0)`, `CHECK (lower_bound <= upper_bound)`), and explicit FOREIGN KEY ON DELETE RESTRICT.',
      'JSONB Usage Policy: Reserved exclusively for raw upstream vendor payloads and variable polymorphic metadata; forbidden for relational financial columns.'
    ],
    architectureNotes: 'TypeScript mapping handled via strict Prisma / Drizzle type-safe schema definitions with zero runtime float conversions.'
  },
  {
    code: 'E',
    titleEn: 'Global Geography Model (ISO 3166-1/2)',
    titleFr: 'Modèle Géographique Mondial',
    category: 'Strategy & Domains',
    summary: 'Universal geographic repository covering 249 sovereign countries, overseas territories, regional economic pacts, and subdivisions.',
    details: [
      'Entities: `Country`, `CountryTranslation`, `AdministrativeSubdivision`, `RegionPack`, `EconomicZone`.',
      'ISO 3166-1 alpha-2, alpha-3, numeric codes, UN M49 regional codes, and IANA primary timezone identifiers.',
      'Polymorphic translation table (`CountryTranslation`) supporting 10+ languages without column bloat.',
      'Subdivision abstraction: First-class support for US States, Canadian Provinces, German Länder, Swiss Cantons, UK Devolved Nations, and UAE Emirates.'
    ],
    architectureNotes: 'Decoupled from YamooPay commercial market availability to ensure complete universal geographic awareness from Day 1.'
  },
  {
    code: 'F',
    titleEn: 'Jurisdiction & Hierarchy Model',
    titleFr: 'Modèle des Juridictions & Hiérarchies',
    category: 'PostgreSQL & Schema',
    summary: 'Multi-tiered recursive hierarchy supporting overlapping tax, legal, and banking jurisdictions across federal, state, and municipal layers.',
    details: [
      'Jurisdiction Types: SUPRANATIONAL (EU, CEMAC), FEDERAL / COUNTRY, STATE / PROVINCE, CANTON, MUNICIPAL / CITY, SPECIAL_ECONOMIC_ZONE.',
      'Hierarchical self-referencing tree: `parent_jurisdiction_id REFERENCES jurisdictions(id)`.',
      'Temporal validity bounds on jurisdictions: `valid_from DATE NOT NULL`, `valid_to DATE`.',
      'Composite tax resolution: Dynamic inheritance allowing calculations to stack Federal + State + Local taxes seamlessly.'
    ],
    architectureNotes: 'Enables resolving complex tax stacks (e.g., US Federal IRS + California FTB + San Francisco Gross Receipts Tax) deterministically.'
  },
  {
    code: 'G',
    titleEn: 'Currency Model (ISO 4217 & Precision)',
    titleFr: 'Modèle Monétaire & Précision',
    category: 'Strategy & Domains',
    summary: 'Universal currency repository supporting 170+ active ISO 4217 fiat currencies, historical currencies, precision rules, and minor units.',
    details: [
      'Minor Unit Metadata: Explicit definition of decimals (USD = 2, EUR = 2, JPY = 0, BHD = 3, CLF = 4).',
      'Currency Types: FIAT (Sovereign central bank issued), SPECIAL (IMF SDR, Gold ounce reference).',
      'Temporal mapping: `CountryCurrency` with `effective_from` / `effective_to` to model currency adoptions (e.g. Eurozone accessions, Croatian Kuna replacement).',
      'Currency Formatting Rules: Standardized spacing, symbol placement (prefix vs suffix), and grouping separators per locale.'
    ],
    architectureNotes: 'Guarantees zero rounding errors across zero-decimal currencies (JPY, KRW, XAF) and 3-decimal currencies (KWD, BHD, OMR).'
  },
  {
    code: 'H',
    titleEn: 'Exchange Rate Data Model & Cross-Rate Engine',
    titleFr: 'Modèle de Taux de Change & Moteur Cross-Rate',
    category: 'PostgreSQL & Schema',
    summary: 'Institutional FX time-series architecture distinguishing between Mid-Market wholesale, Central Bank reference, Bid/Ask, and Provider quotes.',
    details: [
      'Standard Rate Direction: Strictly defined as `1 BASE = X QUOTE` (e.g. EUR/USD = 1.0850 means 1 EUR = 1.0850 USD).',
      'Direct Observation vs Derived Cross-Rates: Explicit `is_derived BOOLEAN` flag, recording the triangulation path (e.g., JPY -> USD -> ZAR) and compound timestamp.',
      'Reciprocal Calculations: Computed deterministically as `1 / observed_rate` with explicit reciprocal flags and rounding safety.',
      'Partitioned Time-Series: Daily and hourly rate observations partitioned by month for rapid historical and latest-tick querying.'
    ],
    architectureNotes: 'Materialized latest-rate views (`mv_latest_exchange_rates`) refreshed concurrently every 60 seconds for ultra-low latency frontend reads.'
  },
  {
    code: 'I',
    titleEn: 'Transfer Corridor & Remittance Provider Model',
    titleFr: 'Modèle des Corridors de Transfert & Prestataires',
    category: 'Strategy & Domains',
    summary: 'Multi-corridor international money transfer data model capturing real fees, exchange rate markups, payment methods, and payout delivery times.',
    details: [
      'Entities: `TransferProvider`, `ProviderMarketAvailability`, `TransferCorridor`, `TransferQuoteSnapshot`, `DeliveryMethod`, `PaymentMethod`.',
      'Corridor Definition: Sending Country + Sending Currency -> Receiving Country + Receiving Currency.',
      'Immutability: Historical quote snapshots are append-only; provider price updates generate new timestamped records.',
      'Transparent Fee Breakdown: Explicit separation of upfront transfer fee, payment method surcharge, and FX spread markup vs mid-market benchmark.'
    ],
    architectureNotes: 'Powers the "Compare Money Transfers" tool with transparent, un-manipulated cost calculations.'
  },
  {
    code: 'J',
    titleEn: 'Salary, Payroll & Income Tax Foundation',
    titleFr: 'Fondation Fiscale & Calcul de Salaire',
    category: 'PostgreSQL & Schema',
    summary: 'Normalized schema for progressive tax brackets, social security contributions, standard deductions, and tax allowances across all tier-1 countries.',
    details: [
      'Entities: `TaxRuleSet`, `TaxBracket`, `ContributionRule`, `DeductionRule`, `ExemptionThreshold`.',
      'Progressive vs Flat Tax Curves: Ordered bracket index with `lower_bound`, `upper_bound`, `marginal_rate NUMERIC(8,6)`, and optional `fixed_base_amount`.',
      'Employee vs Employer Split: Separate social charge rules (e.g. US FICA/Medicare, UK NI Class 1, French Cotisations Sociales Salariales / Patronales).',
      'Filing Status Support: Single, Married Joint, Married Separate, Head of Household configurations per jurisdiction.'
    ],
    architectureNotes: 'All rates verified directly against official statutory publications (IRS Pub 15-T, HMRC PAYE, DGFiP, CRA T4032).'
  },
  {
    code: 'K',
    titleEn: 'Lending & Mortgage Data Foundation',
    titleFr: 'Fondation des Données de Prêt & Crédit',
    category: 'Strategy & Domains',
    summary: 'Actuarial lending models for standard amortization, adjustable rate benchmarks, APR calculations, and early prepayment penalty caps.',
    details: [
      'Entities: `LendingMarket`, `InterestRateObservation`, `LoanProductType`, `AmortizationScheduleProfile`.',
      'Benchmark Rates: Tracking SOFR, EURIBOR, SONIA, and Prime Rate observations with central bank source provenance.',
      'Calculation Invariants: Exact French amortization formula with precision validation and total interest caps.',
      'Jurisdiction Caps: Maximum legal usury rate (taux d\'usure) and statutory prepayment penalty ceilings (indemnités de remboursement anticipé).'
    ],
    architectureNotes: 'Ensures deterministic comparison between nominal interest rates and full APR / TAEG (Taux Annuel Effectif Global).'
  },
  {
    code: 'L',
    titleEn: 'Data Source Catalog & Authority Hierarchy',
    titleFr: 'Catalogue des Sources & Hiérarchie d\'Autorité',
    category: 'Governance & Security',
    summary: 'Comprehensive registry of all data providers, institutional ranking rules, licensing terms, and automated health telemetry.',
    details: [
      'Source Categories: Central Banks (ECB, Fed, BoE, BoC), Tax Authorities (IRS, HMRC, DGFiP, CRA, ATO), Verified Aggregators (Wise, OFX, XE, World Bank).',
      'Authority Hierarchy: Statutory Government source automatically supersedes secondary aggregator on regulatory data.',
      'Licensing & Terms: Tracking attribution requirements, commercial usage authorizations, and contractual review dates.',
      'Zero Hardcoded Secrets: API credentials managed strictly via Dokploy environment variables and secure secret stores.'
    ],
    architectureNotes: 'Automated health pings calculate uptime, latency, error rate, and auto-degrade failing sources.'
  },
  {
    code: 'M',
    titleEn: 'Raw -> Staging -> Canonical Pipeline',
    titleFr: 'Pipeline Données Brutes -> Staging -> Canonique',
    category: 'Pipeline & Quality',
    summary: 'Multi-stage data ingestion architecture preventing unvalidated external payloads from entering productive tables.',
    details: [
      '1. RAW Layer: Ingest raw JSON/XML response payload with cryptographic SHA-256 hash, HTTP status, and ingestion run ID.',
      '2. STAGING Layer: Parse into typed intermediate schemas and execute pre-promotion validation suites.',
      '3. PROMOTION GATE: Verify currency ISO codes, jurisdiction bounds, positive numeric bounds, and freshness SLAs.',
      '4. CANONICAL Layer: Insert / Upsert into official System of Record PostgreSQL tables with full provenance metadata.',
      '5. SERVING / READ Layer: Materialize optimized read views and push to high-speed cache.'
    ],
    architectureNotes: 'Guarantees that third-party schema shifts or upstream API errors trigger quarantine rather than silent database corruption.'
  },
  {
    code: 'N',
    titleEn: 'Data Quarantine & Anomaly Detection',
    titleFr: 'Quarantaine des Données & Détection d\'Anomalies',
    category: 'Pipeline & Quality',
    summary: 'Isolated holding queue for corrupted, suspicious, or statistically anomalous external data with full triage workflows.',
    details: [
      'Triggers: Out-of-bounds FX rate changes (>5% spike in 1 hour), negative money amounts, unknown ISO currency codes, expired tax bracket dates.',
      'Quarantine Queue: `data_quarantine` table storing original payload, failed validation rule, severity level, and quarantine timestamp.',
      'Triage Workflow: Operator UI allowing authorized data engineers to Inspect, Resolve with Manual Override, or Discard.',
      'Audit Trail: Every quarantine resolution requires mandatory user attribution and justification comments.'
    ],
    architectureNotes: 'Prevents rogue API bugs (e.g. upstream feed broadcasting EUR/USD = 0.0001) from contaminating live user tools.'
  },
  {
    code: 'O',
    titleEn: 'Freshness Engine & SLA Policies',
    titleFr: 'Moteur de Fraîcheur & Politiques de SLA',
    category: 'Pipeline & Quality',
    summary: 'Domain-specific Data Freshness Engine enforcing strict real-time and statutory time-to-live (TTL) thresholds.',
    details: [
      'Status Spectrum: Standardized across FRESH, AGING, STALE, EXPIRED, UNAVAILABLE, and FAILED.',
      'Domain SLAs: FX Market Rates (Fresh < 60s, Stale > 5m), Central Bank Reference Rates (Fresh < 24h, Stale > 36h), Tax Rulesets (Fresh < 365d, Stale at tax year boundary).',
      'Fallback Policy: Automated fallback to secondary verified source or Last Known Good (LKG) snapshot when primary source becomes stale.',
      'UI Freshness Badges: Frontend receives explicit freshness metadata to render live trust indicators (PROMPT 02 DataFreshnessBadge).'
    ],
    architectureNotes: 'No financial calculation displays "Live" unless the stream meets strict real-time telemetry verification.'
  },
  {
    code: 'P',
    titleEn: 'Data Quality Engine (7 Dimensions)',
    titleFr: 'Moteur de Qualité des Données (7 Dimensions)',
    category: 'Pipeline & Quality',
    summary: 'Automated data quality monitoring checking Completeness, Validity, Uniqueness, Consistency, Timeliness, Accuracy, and Referential Integrity.',
    details: [
      '1. Completeness: Zero unexpected null values in mandatory financial fields.',
      '2. Validity: Numeric bounds, ISO formatting, valid enum types, and mathematical schema constraints.',
      '3. Uniqueness: Fingerprint hash collision detection and deduplication across identical ingestion batches.',
      '4. Consistency: Cross-source coherence (e.g. cross-rates matching direct market observation within tolerance).',
      '5. Timeliness: Ingestion run latency matching configured refresh schedules.',
      '6. Accuracy: Statistical plausibility checks against historical volatility bands.',
      '7. Referential Integrity: Strict foreign key compliance across all dimensional tables.'
    ],
    architectureNotes: 'Runs automated validation jobs after every ingestion batch; produces quality scorecards for the Admin Command Center.'
  },
  {
    code: 'Q',
    titleEn: 'Bitemporal & Ruleset Versioning Strategy',
    titleFr: 'Stratégie Bitemporelle & Versionnement',
    category: 'Governance & Security',
    summary: 'Comprehensive temporal modeling distinguishing between Valid Time (real-world statutory effect) and System Time (database ingestion).',
    details: [
      'Valid Time: `effective_from DATE NOT NULL` and `effective_to DATE` (when legislation is legally in force).',
      'System Time: `created_at TIMESTAMPTZ NOT NULL` and `superseded_at TIMESTAMPTZ` (when YamooPay recorded the rule).',
      'Scheduled Future Legislation: Ability to ingest 2027 tax rules in advance with `SCHEDULED` status, auto-activating on Jan 1, 2027.',
      'Prohibition of In-Place Updates: Regulatory tax rules are strictly append-only; updates create a new version superseding the prior record.'
    ],
    architectureNotes: 'Ensures 100% calculation reproducibility: recalculating a 2025 paycheck yields the identical historic result regardless of current year.'
  },
  {
    code: 'R',
    titleEn: 'Four-Eyes Principle (Maker / Checker Workflow)',
    titleFr: 'Principe des 4 Yeux (Workflow Maker / Checker)',
    category: 'Governance & Security',
    summary: 'Mandatory dual-authorization governance for critical statutory tax brackets, exchange source configurations, and manual overrides.',
    details: [
      'Roles: Maker (Data Engineer who proposes / drafts new ruleset), Checker (Senior Tax Lead / Compliance Officer who verifies & approves).',
      'Lifecycle: DRAFT -> REVIEW -> VERIFIED -> SCHEDULED -> ACTIVE -> SUPERSEDED.',
      'Anti-Collusion Enforcement: Database constraint and API logic strictly forbid the creator from self-approving their own change request.',
      'Audit Logging: Immutable record of timestamps, IP addresses, diff comparisons, and legal publication source references.'
    ],
    architectureNotes: 'Eliminates single-operator risk and human entry errors in high-consequence payroll and tax calculation engines.'
  },
  {
    code: 'S',
    titleEn: 'Data Lineage, Provenance & Explainability',
    titleFr: 'Lignage des Données, Provenance & Explicabilité',
    category: 'Pipeline & Quality',
    summary: 'End-to-end data provenance tracking from external raw HTTP payload down to intermediate formula nodes and frontend result cards.',
    details: [
      'Lineage Chain: Data Source -> Ingestion Run -> Raw Record -> Staging Check -> Canonical Record -> Derived Value -> User Calculation Snapshot.',
      'Explainability Engine: Generating dual outputs — machine-readable exact numerical results + human-readable step-by-step statutory formulas.',
      'User Calculation Snapshots: Saved calculations record exact ruleset version, FX observation IDs, and effective date used at runtime.',
      'Lineage Inspector UI: Interactive node graph in the Data Governance Command Center allowing visual audit of any financial metric.'
    ],
    architectureNotes: 'Directly answers "Where did this number come from?" with verifiable mathematical proof.'
  },
  {
    code: 'T',
    titleEn: 'Conflict Management & Multi-Source Arbitration',
    titleFr: 'Gestion des Conflits & Arbitrage Multi-Sources',
    category: 'Pipeline & Quality',
    summary: 'Deterministic arbitration engine resolving discrepancies between multiple trusted upstream data feeds.',
    details: [
      'Conflict Detection: Triggers when two tier-1 sources report exchange rates differing by >0.15% or tax brackets with mismatched thresholds.',
      'Arbitration Rules: 1. Authority Level Hierarchy (Statutory Central Bank > Commercial API), 2. Timestamp Freshness, 3. Historical Reliability Score.',
      'Automatic Arbitration: Resolves minor spreads and logs variance to `data_conflicts`.',
      'Manual Escalation: Significant divergences (>0.5%) generate an alert in the Data Quality Center for human review.'
    ],
    architectureNotes: 'Maintains platform stability during volatile foreign exchange market open/close sessions.'
  },
  {
    code: 'U',
    titleEn: 'Fallback Strategy & Last Known Good (LKG)',
    titleFr: 'Stratégie de Fallback & Dernière Bonne Donnée (LKG)',
    category: 'Pipeline & Quality',
    summary: 'Resilient multi-tier fallback architecture ensuring continuous uptime during upstream provider outages without corrupting data integrity.',
    details: [
      'Fallback Hierarchy: Primary Source -> Secondary Verified Source -> Triangulated Cross-Rate -> Last Known Good (LKG) Snapshot.',
      'LKG Qualification: A record only becomes LKG if it passed 100% of data quality checks; corrupted recent records are rejected.',
      'UI Transparency: When fallback data is active, the frontend clearly displays "Using verified benchmark from [Time UTC]" to maintain user trust.',
      'Circuit Breaker: Automatically trips if a provider fails 3 consecutive syncs, rerouting traffic without waiting for request timeouts.'
    ],
    architectureNotes: 'Guarantees graceful degradation: temporary feed outages never take down user calculator experiences.'
  },
  {
    code: 'V',
    titleEn: 'Append-Only Audit Architecture',
    titleFr: 'Architecture d\'Audit Immuable',
    category: 'Governance & Security',
    summary: 'Tamper-evident, append-only operational audit log tracking all administrative interventions, overrides, ruleset activations, and system events.',
    details: [
      'Schema: `audit.logs` recording `id (UUIDv7)`, `actor_id`, `actor_role`, `action`, `entity_type`, `entity_id`, `before_state JSONB`, `after_state JSONB`, `correlation_id`, `created_at`.',
      'Immutability Enforcement: Database permissions revoke UPDATE and DELETE privileges on the audit schema from standard application users.',
      'Correlation IDs: End-to-end distributed tracing linking user HTTP request, backend database transaction, and ingestion run.',
      'Periodic Hash Chaining: Daily cryptographic merkle root generation for external compliance verification (SOC2 Type II / GDPR audit readiness).'
    ],
    architectureNotes: 'Ensures absolute non-repudiation of administrative actions.'
  },
  {
    code: 'W',
    titleEn: 'Database Security, RLS & Role Hierarchy',
    titleFr: 'Sécurité Base de Données, RLS & Rôles',
    category: 'Governance & Security',
    summary: 'Principle of Least Privilege (PoLP) PostgreSQL role architecture with Row Level Security (RLS) for multi-tenant isolation.',
    details: [
      'PostgreSQL Database Roles: `yamoopay_app` (CRUD on serving tables), `yamoopay_ingestion` (write to staging/raw), `yamoopay_analytics` (read-only on replicas), `yamoopay_migrator` (DDL schema migrations only).',
      'Zero Superuser Runtime: Application services connect strictly via non-superuser credentials.',
      'Row Level Security: Enforced on user private workspaces (`user_saved_calculations`, `user_preferences`) ensuring zero cross-tenant leakage.',
      'Encryption: TLS 1.3 in transit, AES-256 at rest on Hostinger persistent NVMe volumes, field-level encryption for sensitive tokens.'
    ],
    architectureNotes: 'Database credentials strictly injected via environment variables; never checked into version control.'
  },
  {
    code: 'X',
    titleEn: 'Data Privacy, Minimization & Log Redaction',
    titleFr: 'Confidentialité des Données & Masquage des Logs',
    category: 'Governance & Security',
    summary: 'Client-first privacy architecture: financial inputs processed client-side by default with zero unnecessary server logging.',
    details: [
      'Data Minimization: Salary and tax calculators operate 100% client-side; user earnings and deductions are never transmitted to telemetry.',
      'Automated Log Redaction: Regex filters strip salaries, bank balances, tax identifiers, and auth tokens from operational server logs.',
      'Local Storage Workspace: Unauthenticated users save calculations locally in browser `localStorage`; authenticated sync is opt-in.',
      'GDPR & CCPA Compliance: Built-in right to erasure (hard delete of user workspace snapshots) and right to data portability (JSON export).'
    ],
    architectureNotes: 'Eliminates database liability by refusing to collect unneeded personal financial records.'
  },
  {
    code: 'Y',
    titleEn: 'Data Retention & Lifecycle Archival',
    titleFr: 'Rétention des Données & Archivage',
    category: 'Operations & Runbooks',
    summary: 'Tiered lifecycle policies balancing analytical historical value, compliance retention, and PostgreSQL storage efficiency.',
    details: [
      'Raw API Payloads: Retained in PostgreSQL for 30 days, then moved to cold compressed S3/GCS object storage.',
      'Hourly FX Observations: Retained in high-speed tables for 180 days; aggregated into daily OHLC (Open, High, Low, Close) permanent snapshots.',
      'Regulatory Tax Rulesets: Indefinitely retained (permanent historical archive for multi-year retrospective calculations).',
      'Audit Logs: Retained active for 7 years to meet international financial compliance mandates.'
    ],
    architectureNotes: 'Automated pg_cron pruning jobs maintain database lean performance without manual DBA intervention.'
  },
  {
    code: 'Z',
    titleEn: 'Indexing Strategy & Query Budget',
    titleFr: 'Stratégie d\'Indexation & Budget de Requêtes',
    category: 'PostgreSQL & Schema',
    summary: 'Surgical indexing strategy optimized for sub-10ms query latency on latest rates, bracket curves, and country directories.',
    details: [
      'Composite Indexes: `(base_currency_id, quote_currency_id, observed_at DESC)` for instantaneous pair rate lookups.',
      'Temporal Partial Indexes: `CREATE INDEX idx_active_tax_rules ON tax.rulesets (jurisdiction_id, tax_year) WHERE status = \'ACTIVE\';`.',
      'Trigram GIN Indexes: `CREATE INDEX idx_tools_search ON content.tools USING gin (name gin_trgm_ops, description gin_trgm_ops);` for fast fuzzy search.',
      'Query Budget: N+1 query prevention; all primary tool landing endpoints constrained to maximum 2 database roundtrips.'
    ],
    architectureNotes: 'Zero redundant indexes to maintain high write throughput during high-frequency FX rate ingestion batches.'
  },
  {
    code: 'AA',
    titleEn: 'Partitioning Strategy (Time-Series & Quotes)',
    titleFr: 'Stratégie de Partitionnement (Séries Temporelles)',
    category: 'PostgreSQL & Schema',
    summary: 'Native PostgreSQL declarative table partitioning for high-volume observational FX ticks and transfer quote snapshots.',
    details: [
      'Range Partitioning by Month: `exchange.rate_observations` partitioned by `observed_at` (e.g. `rate_observations_y2026m08`).',
      'Automated Partition Maintenance: `pg_partman` / migration script pre-creating 3 months of forward partitions and detaching cold partitions.',
      'Partition Pruning: Query planner automatically skips irrelevant monthly tables for instant single-day and latest-quote lookups.',
      'Local Indexes: Each partition maintains its own isolated index tree, preventing B-tree bloat.'
    ],
    architectureNotes: 'Allows scaling to 100M+ FX observations and provider quotes without database performance degradation.'
  },
  {
    code: 'AB',
    titleEn: 'Migration Strategy & Zero-Downtime DDL',
    titleFr: 'Stratégie de Migration & DDL Sans Interruption',
    category: 'Operations & Runbooks',
    summary: 'Three-phase Expand-Migrate-Contract pattern for zero-downtime database schema evolutions in production.',
    details: [
      'Phase 1 (Expand): Add new nullable columns or new tables; deploy updated backend code writing to both.',
      'Phase 2 (Migrate): Backfill historical data via idempotent batch worker scripts.',
      'Phase 3 (Contract): Switch application reads to new schema; deprecate and remove old columns in subsequent release.',
      'Prohibited Commands: Disallow `prisma db push` or raw blocking `ALTER TABLE ... ADD COLUMN ... DEFAULT (heavy)` without locks in production.'
    ],
    architectureNotes: 'Every migration file is paired with an automated forward test and safe rollback script.'
  },
  {
    code: 'AC',
    titleEn: 'Backup, Point-in-Time Recovery (PITR) & RPO/RTO',
    titleFr: 'Sauvegarde, Restauration PITR & Objectifs RPO/RTO',
    category: 'Operations & Runbooks',
    summary: 'Disaster recovery framework targeting RPO < 5 minutes and RTO < 30 minutes on Hostinger VPS infrastructure.',
    details: [
      'Daily Automated Physical Backups: Compressed `pg_dump` and continuous WAL (Write-Ahead Log) archiving to off-site cloud storage.',
      'Point-in-Time Recovery (PITR): Ability to replay WAL logs and restore database state to any specific second in the prior 14 days.',
      'Quarterly Restore Drills: Automated recovery verification tests restoring backups onto staging to validate archive integrity.',
      'RPO Target: Maximum 5 minutes of observational data loss in cataclysmic server loss (zero regulatory tax rule loss).'
    ],
    architectureNotes: 'Documented disaster runbook for rapid VPS rebuild and container reprovisioning via Dokploy.'
  },
  {
    code: 'AD',
    titleEn: 'Data Observability, Telemetry & SRE Metrics',
    titleFr: 'Observabilité des Données & Métriques SRE',
    category: 'Operations & Runbooks',
    summary: 'Comprehensive data pipeline telemetry tracking ingestion health, latency percentiles, error rates, and quality scorecards.',
    details: [
      'Key Performance Indicators: `ingestion_success_rate` (>99.9%), `source_latency_p95` (<450ms), `stale_dataset_count` (0), `quarantined_records_count`.',
      'Alert Tiers: SEV1 (All FX sources down / corrupted active tax ruleset), SEV2 (Single tier-1 source degraded), SEV3 (Quarantine spike >50 items), SEV4 (Informational telemetry anomaly).',
      'Notification Webhooks: Real-time alerting to engineering Slack / Discord channels and SRE pager rotation.',
      'Data Quality Scorecard: Aggregate health metric (0-100%) visible on the Executive Command Center.'
    ],
    architectureNotes: 'Transforms silent data failures into immediate, actionable operational alerts.'
  },
  {
    code: 'AE',
    titleEn: 'Data Quality Command Center (Admin UI)',
    titleFr: 'Centre de Commande Qualité des Données (Admin)',
    category: 'Operations & Runbooks',
    summary: 'Institutional web-based console providing real-time oversight of all data pipelines, sources, freshness SLAs, and quarantine queues.',
    details: [
      'Real-Time KPI Ribbons: Ingestion Success Rate, Active Sources, Fresh Datasets, Quarantined Records, Open Conflicts, Expiring Rulesets.',
      'Interactive Schema & Table Dictionary: Live schema browser with column constraints, index lists, and DDL SQL inspector.',
      'Quarantine Triage Manager: Table of flagged items with single-click manual inspect, payload review, and resolution buttons.',
      'Source Health Matrix: Latency sparklines, uptime badges, and API status toggles for every upstream partner.'
    ],
    architectureNotes: 'Built entirely in strict Light Mode adhering to Prompt 02 design tokens.'
  },
  {
    code: 'AF',
    titleEn: 'Global Coverage & Market Readiness Matrix',
    titleFr: 'Matrice de Couverture Mondiale & Préparation',
    category: 'Strategy & Domains',
    summary: 'Strategic multi-wave expansion matrix tracking data completeness and launch gates across 35+ countries and 6 tool categories.',
    details: [
      'Wave 1 (Priority): United States, Canada (100% Live Certified).',
      'Wave 2 (Europe): United Kingdom, France, Germany, Spain, Italy, Netherlands, Switzerland, Belgium, Ireland (Live / Beta).',
      'Wave 3 (Asia-Pacific): Japan, Australia, Singapore, India, Hong Kong, South Korea, New Zealand (Live / Staging).',
      'Wave 4 (Middle East & Latin America): UAE, Saudi Arabia, Brazil, Mexico, Chile (Staging / Planned).',
      'Wave 5 (Africa): Nigeria, South Africa, Kenya, Ghana, Cameroon, Senegal, Ivory Coast, Morocco, Egypt (Global-First Foundations Active).'
    ],
    architectureNotes: 'A market cannot be switched to "LIVE" without satisfying the automated 5-point Data Readiness Gate.'
  },
  {
    code: 'AG',
    titleEn: 'Data Readiness & Launch Gate System',
    titleFr: 'Système de Préparation des Données & Portes de Lancement',
    category: 'Governance & Security',
    summary: 'Strict quantitative criteria required before any financial calculator can be activated in a new country jurisdiction.',
    details: [
      'Gate 1 (Statutory Source Coverage): Official government tax publication on file and verified.',
      'Gate 2 (Quality Benchmark): Minimum 99.0% pass rate across all 7 data quality dimensions.',
      'Gate 3 (Deterministic Test Fixtures): Comprehensive test suite verifying edge-case bracket curves against official tax forms.',
      'Gate 4 (Methodology Transparency): Published public methodology documentation with mathematical formula breakdown.',
      'Gate 5 (Observability & SRE): Automated freshness monitoring and fallback failover rules active in production.'
    ],
    architectureNotes: 'Enforces "Correctness Before Coverage" — prevents publishing half-baked or uncertified financial tools.'
  },
  {
    code: 'AH',
    titleEn: 'Interactive Entity-Relationship Diagram (ERD)',
    titleFr: 'Diagramme Entité-Relationnel Interactif (ERD)',
    category: 'PostgreSQL & Schema',
    summary: 'Visual and structural architecture mapping all 28 core tables and their relational foreign keys across 12 domain schemas.',
    details: [
      'Core Schema Clusters: Geography Cluster (5 tables), Currency & FX Cluster (5 tables), Tax & Salary Cluster (6 tables), Transfers & Lending Cluster (4 tables), Pipeline & Quality Cluster (8 tables).',
      'Referential Integrity: All relations enforce foreign key cascades or strict delete locks.',
      'Cardinality Mapping: Explicit 1:1, 1:N, and N:M representations with join tables.',
      'Interactive Schema Explorer: Direct UI navigation between tables, foreign keys, and indexes.'
    ],
    architectureNotes: 'Serves as the definitive engineering blueprint for database developers and backend API architects.'
  },
  {
    code: 'AI',
    titleEn: 'PostgreSQL Table & Column Dictionary',
    titleFr: 'Dictionnaire des Tables & Colonnes PostgreSQL',
    category: 'PostgreSQL & Schema',
    summary: 'Comprehensive data dictionary detailing exact data types, nullability, constraints, default values, and business descriptions.',
    details: [
      'Over 150+ columns documented across all production tables.',
      'Strict type annotations: UUID, NUMERIC(18,4), TIMESTAMPTZ, VARCHAR(64), BOOLEAN, JSONB.',
      'Explicit constraint documentation: CHECK bounds, UNIQUE indexes, and FOREIGN KEY references.',
      'Domain categorization and data classification tagging (PUBLIC, INTERNAL, CONFIDENTIAL, SENSITIVE).'
    ],
    architectureNotes: 'Guarantees complete onboarding clarity for newly joining platform and data engineers.'
  },
  {
    code: 'AJ',
    titleEn: 'Index & Partitioning Dictionary',
    titleFr: 'Dictionnaire des Index & du Partitionnement',
    category: 'PostgreSQL & Schema',
    summary: 'Exhaustive inventory of all database B-tree, Partial, Unique, and GIN Trigram indexes across the YamooPay cluster.',
    details: [
      'Documented query access patterns justifying every created index.',
      'Write-overhead trade-off analysis for high-velocity tables.',
      'Maintenance guidelines: Automated index bloat monitoring and `REINDEX CONCURRENTLY` schedules.',
      'Partition index local vs global behavior specifications.'
    ],
    architectureNotes: 'Prevents index sprawl and maintains lean memory footprint in shared buffer caches.'
  },
  {
    code: 'AK',
    titleEn: 'End-to-End Data Flow Diagrams',
    titleFr: 'Diagrammes de Flux de Données de Bout en Bout',
    category: 'Pipeline & Quality',
    summary: 'Complete architectural data flow maps documenting the journey from external upstream APIs to user calculator renders.',
    details: [
      'Flow 1: Scheduled FX Sync (ECB / Central Bank API -> Ingestion Worker -> Staging Validation -> Canonical Database -> Materialized View -> Frontend Client).',
      'Flow 2: Regulatory Tax Year Update (Tax Authority PDF/Law -> Maker Staging Ingestion -> Automated Quality Checks -> Checker Review -> Four-Eyes Approval -> Scheduled Activation).',
      'Flow 3: Remittance Quote Request (User Query -> Live Corridor Aggregation -> Fee Normalization -> Spread Math -> Frontend Result Matrix).',
      'Flow 4: Anomaly Quarantine & Triage (Corrupt Payload -> Anomaly Trigger -> Quarantine Table -> SRE Notification -> Admin Triage Action).'
    ],
    architectureNotes: 'Illustrates how asynchronous background workers interact with synchronous client API routes.'
  },
  {
    code: 'AL',
    titleEn: 'Source Ingestion Sequence Diagrams',
    titleFr: 'Diagrammes de Séquence d\'Ingestion des Sources',
    category: 'Pipeline & Quality',
    summary: 'Micro-level sequence diagrams detailing HTTP headers, retry backoff algorithms, rate limit budgeting, and signature verification.',
    details: [
      'Step 1: Orchestrator triggers cron job with distributed advisory lock (`pg_try_advisory_lock`).',
      'Step 2: Adapter performs authenticated HTTPS fetch with exponential jitter backoff (max 3 retries).',
      'Step 3: Response payload is SHA-256 hashed and committed to `raw_data_records`.',
      'Step 4: Staging validator parses schema against Zod/DB contract.',
      'Step 5: Database transaction promotes valid rows to canonical tables; failed rows move to quarantine.',
      'Step 6: Ingestion Run record finalized with records received/accepted/quarantined counts and duration metrics.'
    ],
    architectureNotes: 'Guarantees 100% idempotent execution — running a job twice never duplicates records or creates contradictory rates.'
  },
  {
    code: 'AM',
    titleEn: 'Data Quality & Validation Workflows',
    titleFr: 'Workflows de Validation & Qualité des Données',
    category: 'Pipeline & Quality',
    summary: 'Standard operating procedures for continuous data quality validation, automated assertion testing, and regression prevention.',
    details: [
      'Pre-Ingestion Assertions: Basic schema structure, content-length, non-empty payload.',
      'Post-Ingestion Assertions: Mathematical consistency, boundary values, zero-rate checks, standard deviation bounds.',
      'Daily Re-Validation: Scanning all active tax rulesets and exchange rates for date expirations or orphan foreign keys.',
      'Automated Report Card Generation: Summaries pushed daily to Data Platform leadership.'
    ],
    architectureNotes: 'Continuous data testing inspired by Great Expectations and DBT test methodologies.'
  },
  {
    code: 'AN',
    titleEn: 'Four-Eyes Approval & Change Request Workflows',
    titleFr: 'Workflows d\'Approbation & Demandes de Changement',
    category: 'Governance & Security',
    summary: 'Step-by-step regulatory change workflow governing how new statutory tax rules and provider configurations reach production.',
    details: [
      'Step 1: Data Engineer drafts change request in Admin Console (`DataChangeRequest`).',
      'Step 2: System runs automated test fixtures against proposed bracket changes, generating side-by-side diffs.',
      'Step 3: Senior Tax Lead reviews statutory source citation and test outputs.',
      'Step 4: Checker submits cryptographic sign-off with secondary credentials.',
      'Step 5: System transitions ruleset to `SCHEDULED` (or `ACTIVE` if effective immediately) and writes immutable audit entry.'
    ],
    architectureNotes: 'Provides institutional governance required by financial regulators and enterprise partners.'
  },
  {
    code: 'AO',
    titleEn: 'Data Incident Management & Runbooks',
    titleFr: 'Gestion des Incidents Données & Runbooks',
    category: 'Operations & Runbooks',
    summary: 'Operational runbooks for rapid incident containment, data corruption rollback, and emergency fallback activation.',
    details: [
      'Runbook 1 (Primary FX Source Outage): 1. Confirm source status, 2. Verify auto-failover to secondary provider, 3. Check UI banner, 4. Engage vendor SRE, 5. Restore and backfill.',
      'Runbook 2 (Corrupted Tax Bracket Published): 1. Trigger Emergency Safe Mode for affected country, 2. Roll back to prior verified ruleset version via single transaction, 3. Re-run calculation regression tests, 4. Clear edge caches, 5. Publish post-mortem.',
      'Runbook 3 (Quarantine Flood): 1. Inspect common error signature in quarantine queue, 2. Identify upstream API breaking contract change, 3. Update adapter schema parser, 4. Re-process quarantined items.',
      'Runbook 4 (Database Connection Saturation): 1. Inspect active queries via `pg_stat_activity`, 2. Terminate long-running blocking locks, 3. Scale PgBouncer pool limits, 4. Optimize offending endpoint.'
    ],
    architectureNotes: 'Empowers any on-call engineer to resolve critical financial data incidents within strict SLA windows.'
  },
  {
    code: 'AP',
    titleEn: 'Backend Module & Service Architecture',
    titleFr: 'Structure des Modules Backend & Services',
    category: 'Strategy & Domains',
    summary: 'Modular Monolith architecture for clean separation of concerns without premature microservice complexity.',
    details: [
      'Directory Structure: `src/modules/geography`, `src/modules/currency`, `src/modules/exchange`, `src/modules/tax`, `src/modules/transfers`, `src/modules/governance`, `src/modules/quality`, `src/db`.',
      'Domain Repositories: Dedicated type-safe repositories (`CurrencyRepository`, `ExchangeRateRepository`, `TaxRuleRepository`) isolating raw SQL.',
      'Financial Calculation Engines: Pure deterministic mathematical libraries (`src/lib/financial/`) decoupled from database queries for maximum testability.',
      'Transactional Outbox Pattern: Preparing event-driven capabilities without external messaging broker dependencies.'
    ],
    architectureNotes: 'Maintainable, testable, and directly runnable on Hostinger VPS Node.js runtime.'
  },
  {
    code: 'AQ',
    titleEn: 'Transactional Outbox & Event Readiness',
    titleFr: 'Pattern Outbox Transactionnel & Événements',
    category: 'Pipeline & Quality',
    summary: 'Reliable event-publishing architecture guaranteeing that database mutations and external notifications remain perfectly synchronized.',
    details: [
      'Outbox Table: `operations.outbox_events` storing event type, aggregate ID, payload JSONB, and published status.',
      'Atomic Transaction: Data change and outbox event write occur within the exact same PostgreSQL `BEGIN ... COMMIT` block.',
      'Outbox Worker: Background process polling unpublished events and dispatching to webhooks, cache invalidation, and telemetry.',
      'Zero Dual-Write Hazard: Eliminates risk of database committing while event publishing fails silently.'
    ],
    architectureNotes: 'Enables future event streaming to Redis or Kafka without refactoring domain business logic.'
  },
  {
    code: 'AR',
    titleEn: 'Hostinger VPS & Dokploy Infrastructure Blueprint',
    titleFr: 'Plan d\'Infrastructure Hostinger VPS & Dokploy',
    category: 'Operations & Runbooks',
    summary: 'Production-ready hosting architecture utilizing Hostinger KVM VPS, Dokploy application manager, and isolated Docker network bridge.',
    details: [
      'Docker Compose Stack: App Service (Node.js/Next.js), PostgreSQL 18 Service (with persistent volume mount `/var/lib/postgresql/data`), PgBouncer Service, Cron Worker Service.',
      'Resource Allocations: Memory limits, CPU reservations, and restart policies (`restart: unless-stopped`).',
      'SSL / Reverse Proxy: Traefik / Nginx reverse proxy managed automatically by Dokploy with automated Let\'s Encrypt TLS certs.',
      'Host-Level Backup Automation: Automated nightly snapshot of Postgres data volume to remote backup storage.'
    ],
    architectureNotes: 'Cost-effective, sovereign, enterprise-grade deployment capable of handling millions of monthly visits.'
  },
  {
    code: 'AS',
    titleEn: 'Data Governance Acceptance & Verification Criteria',
    titleFr: 'Critères d\'Acceptation & Vérification de Gouvernance',
    category: 'Governance & Security',
    summary: 'The 10-Question Litmus Test applied to every critical financial dataset and calculation before production sign-off.',
    details: [
      '1. Who owns it? (Identified Data Domain Lead on file).',
      '2. Where does it come from? (Statutory Source ID and URL registered).',
      '3. When was it observed? (Exact UTC observation timestamp recorded).',
      '4. When is it valid? (Bitemporal valid_from and valid_to explicitly bounded).',
      '5. Is it fresh? (Freshness Engine reports FRESH status within configured TTL).',
      '6. Is it verified? (Four-eyes Maker/Checker approval signature present).',
      '7. Has it changed? (Immutable versioning with cryptographic fingerprint).',
      '8. Can we reproduce it? (Saved calculation snapshot replicates exact result).',
      '9. Can we explain it? (Human-readable formula breakdown published).',
      '10. Can we roll it back? (Prior superseded version instantly reactivatable).'
    ],
    architectureNotes: 'Any metric failing even one question is barred from production serving.'
  },
  {
    code: 'AT',
    titleEn: 'Prioritized Implementation Backlog (P0 -> P2)',
    titleFr: 'Backlog d\'Implémentation Priorisé (P0 -> P2)',
    category: 'Operations & Runbooks',
    summary: 'Phased engineering roadmap sequencing schema deployment, pipeline automation, governance UI, and regional rollouts.',
    details: [
      'Phase P0 (Foundational Core - Immediate): PostgreSQL 18 schemas (geography, currency, exchange, tax, ingestion, audit), Data Source Catalog, Freshness Engine, Data Quality Engine, Admin Command Center.',
      'Phase P1 (Expansion & Ingestion): Automated ECB / Fed ingestion workers, Remittance corridor aggregation, US/CA/UK/FR/DE tax ruleset seeding, Four-eyes change workflow.',
      'Phase P2 (Enterprise & Scale): Declarative table partitioning for FX ticks, PITR automated backup drills, Outbox worker, Multi-region read replica deployment.'
    ],
    architectureNotes: 'Structured for disciplined, incremental execution by the engineering team.'
  }
];

// ============================================================================
// 2. DATA SOURCE CATALOG (STATUTORY & VERIFIED INSTITUTIONS)
// ============================================================================

export const DATA_SOURCES_CATALOG: DataSourceItem[] = [
  {
    id: 'src-ecb-01',
    name: 'European Central Bank (ECB) Reference Rates',
    provider: 'European Central Bank',
    domain: 'Foreign Exchange (Euro Benchmark)',
    type: 'CENTRAL_BANK',
    baseUrl: 'https://data-api.ecb.europa.eu/service/data/EXR',
    officialWebsite: 'https://www.ecb.europa.eu',
    authorityLevel: 'STATUTORY',
    reliabilityScore: 99.98,
    licenseType: 'Public Domain / Free for Commercial Use with Attribution',
    termsReference: 'ECB Data Licensing Framework 2026',
    commercialUsageAllowed: true,
    attributionRequired: true,
    refreshStrategy: 'Daily at 16:00 CET on TARGET2 Business Days',
    defaultTTLMinutes: 1440,
    active: true,
    currentStatus: 'HEALTHY',
    latencyMs: 184,
    lastSuccessAt: '2026-08-24 16:05:12 UTC',
    lastHttpStatus: 200,
    recordsImportedToday: 42
  },
  {
    id: 'src-fed-02',
    name: 'US Federal Reserve (FRED) Economic Data',
    provider: 'Federal Reserve Bank of St. Louis',
    domain: 'Macroeconomics & US Benchmark Rates',
    type: 'CENTRAL_BANK',
    baseUrl: 'https://api.stlouisfed.org/fred/series/observations',
    officialWebsite: 'https://fred.stlouisfed.org',
    authorityLevel: 'STATUTORY',
    reliabilityScore: 99.95,
    licenseType: 'Public Domain / US Government Work',
    termsReference: 'FRED Terms of Service',
    commercialUsageAllowed: true,
    attributionRequired: true,
    refreshStrategy: 'Daily / Continuous on Market Days',
    defaultTTLMinutes: 720,
    active: true,
    currentStatus: 'HEALTHY',
    latencyMs: 142,
    lastSuccessAt: '2026-08-24 17:30:00 UTC',
    lastHttpStatus: 200,
    recordsImportedToday: 18
  },
  {
    id: 'src-boe-03',
    name: 'Bank of England Daily Spot Rates',
    provider: 'Bank of England',
    domain: 'Foreign Exchange (GBP Benchmark)',
    type: 'CENTRAL_BANK',
    baseUrl: 'https://www.bankofengland.co.uk/boeapps/database/rates',
    officialWebsite: 'https://www.bankofengland.co.uk',
    authorityLevel: 'STATUTORY',
    reliabilityScore: 99.92,
    licenseType: 'Open Government Licence v3.0',
    termsReference: 'OGL UK Central Bank',
    commercialUsageAllowed: true,
    attributionRequired: true,
    refreshStrategy: 'Daily at 16:30 London Time',
    defaultTTLMinutes: 1440,
    active: true,
    currentStatus: 'HEALTHY',
    latencyMs: 210,
    lastSuccessAt: '2026-08-24 15:35:44 UTC',
    lastHttpStatus: 200,
    recordsImportedToday: 26
  },
  {
    id: 'src-irs-04',
    name: 'Internal Revenue Service (IRS) Tax Brackets & Tables',
    provider: 'United States Department of the Treasury',
    domain: 'Income Tax & Federal Payroll Schedules',
    type: 'GOVERNMENT',
    baseUrl: 'https://www.irs.gov/publications/p15t',
    officialWebsite: 'https://www.irs.gov',
    authorityLevel: 'STATUTORY',
    reliabilityScore: 100.0,
    licenseType: 'Public Domain / US Federal Government',
    termsReference: 'IRS Publication 15-T (2026 Tax Year)',
    commercialUsageAllowed: true,
    attributionRequired: true,
    refreshStrategy: 'Annual with Emergency Legislative Revisions',
    defaultTTLMinutes: 525600, // 1 Year
    active: true,
    currentStatus: 'HEALTHY',
    latencyMs: 95,
    lastSuccessAt: '2026-08-20 00:00:00 UTC',
    lastHttpStatus: 200,
    recordsImportedToday: 14
  },
  {
    id: 'src-hmrc-05',
    name: 'HM Revenue & Customs (HMRC) Rates & Thresholds',
    provider: 'UK HM Revenue & Customs',
    domain: 'UK PAYE, National Insurance & Income Tax',
    type: 'GOVERNMENT',
    baseUrl: 'https://www.gov.uk/guidance/rates-and-thresholds-for-employers',
    officialWebsite: 'https://www.gov.uk/hmrc',
    authorityLevel: 'STATUTORY',
    reliabilityScore: 100.0,
    licenseType: 'Open Government Licence v3.0',
    termsReference: 'HMRC Statutory Payroll Regulations 2026/27',
    commercialUsageAllowed: true,
    attributionRequired: true,
    refreshStrategy: 'Annual (UK Fiscal Year April 6 Boundary)',
    defaultTTLMinutes: 525600,
    active: true,
    currentStatus: 'HEALTHY',
    latencyMs: 110,
    lastSuccessAt: '2026-08-20 00:00:00 UTC',
    lastHttpStatus: 200,
    recordsImportedToday: 10
  },
  {
    id: 'src-dgfip-06',
    name: 'Direction Générale des Finances Publiques (DGFiP)',
    provider: 'Ministère de l\'Économie et des Finances (France)',
    domain: 'Barème Impôt sur le Revenu & Cotisations URSSAF',
    type: 'GOVERNMENT',
    baseUrl: 'https://www.impots.gouv.fr/particulier/baremes-fiscaux',
    officialWebsite: 'https://www.impots.gouv.fr',
    authorityLevel: 'STATUTORY',
    reliabilityScore: 100.0,
    licenseType: 'Licence Ouverte v2.0 (Etalab)',
    termsReference: 'Loi de Finances 2026 & Barème Progressif IR',
    commercialUsageAllowed: true,
    attributionRequired: true,
    refreshStrategy: 'Annual with Social Security Adjustment Decree',
    defaultTTLMinutes: 525600,
    active: true,
    currentStatus: 'HEALTHY',
    latencyMs: 135,
    lastSuccessAt: '2026-08-20 00:00:00 UTC',
    lastHttpStatus: 200,
    recordsImportedToday: 8
  },
  {
    id: 'src-wise-07',
    name: 'Wise Transparent Comparison API',
    provider: 'Wise Payments Ltd',
    domain: 'Remittance Corridor Quotes & Real FX Spreads',
    type: 'OFFICIAL_PROVIDER',
    baseUrl: 'https://api.wise.com/v1/comparisons',
    officialWebsite: 'https://wise.com',
    authorityLevel: 'INSTITUTIONAL',
    reliabilityScore: 99.85,
    licenseType: 'Commercial Partner API Agreement',
    termsReference: 'Wise Platform Partner Terms 2026',
    commercialUsageAllowed: true,
    attributionRequired: true,
    refreshStrategy: 'Every 5 Minutes / Live on Corridor Query',
    defaultTTLMinutes: 10,
    active: true,
    currentStatus: 'HEALTHY',
    latencyMs: 245,
    lastSuccessAt: '2026-08-24 17:42:10 UTC',
    lastHttpStatus: 200,
    recordsImportedToday: 1240
  },
  {
    id: 'src-xe-08',
    name: 'XE Commercial FX Wholesale Feed',
    provider: 'XE.com Inc / Euronet Worldwide',
    domain: 'Secondary Mid-Market FX Verification & Fallback',
    type: 'COMMERCIAL_API',
    baseUrl: 'https://xecdapi.xe.com/v1/convert_from',
    officialWebsite: 'https://www.xe.com',
    authorityLevel: 'AGGREGATED',
    reliabilityScore: 99.78,
    licenseType: 'Commercial API Enterprise License',
    termsReference: 'XE Data Services SLA Tier 1',
    commercialUsageAllowed: true,
    attributionRequired: false,
    refreshStrategy: 'Every 60 Seconds',
    defaultTTLMinutes: 5,
    active: true,
    currentStatus: 'HEALTHY',
    latencyMs: 165,
    lastSuccessAt: '2026-08-24 17:44:00 UTC',
    lastHttpStatus: 200,
    recordsImportedToday: 1440
  }
];

// ============================================================================
// 3. LIVE INGESTION RUNS MONITOR
// ============================================================================

export const INGESTION_RUNS_DATA: IngestionRunItem[] = [
  {
    id: 'run-fx-20260824-001',
    sourceId: 'src-ecb-01',
    sourceName: 'European Central Bank (ECB) Reference Rates',
    domain: 'Foreign Exchange',
    startedAt: '2026-08-24 16:00:01 UTC',
    finishedAt: '2026-08-24 16:00:03 UTC',
    status: 'SUCCESS',
    recordsReceived: 42,
    recordsAccepted: 42,
    recordsRejected: 0,
    recordsQuarantined: 0,
    durationMs: 1820,
    correlationId: 'corr-ecb-88392-f01'
  },
  {
    id: 'run-fx-20260824-002',
    sourceId: 'src-xe-08',
    sourceName: 'XE Commercial FX Wholesale Feed',
    domain: 'Foreign Exchange',
    startedAt: '2026-08-24 17:40:00 UTC',
    finishedAt: '2026-08-24 17:40:02 UTC',
    status: 'PARTIAL',
    recordsReceived: 172,
    recordsAccepted: 170,
    recordsRejected: 0,
    recordsQuarantined: 2,
    durationMs: 2150,
    errorSummary: '2 minor currency pairs exceeded volatility spike bounds (>5% in 60m) - quarantined for analysis.',
    correlationId: 'corr-xe-94021-a04'
  },
  {
    id: 'run-remit-20260824-003',
    sourceId: 'src-wise-07',
    sourceName: 'Wise Transparent Comparison API',
    domain: 'Money Transfers',
    startedAt: '2026-08-24 17:35:00 UTC',
    finishedAt: '2026-08-24 17:35:04 UTC',
    status: 'SUCCESS',
    recordsReceived: 120,
    recordsAccepted: 120,
    recordsRejected: 0,
    recordsQuarantined: 0,
    durationMs: 3410,
    correlationId: 'corr-wise-38491-b12'
  },
  {
    id: 'run-tax-20260820-004',
    sourceId: 'src-irs-04',
    sourceName: 'Internal Revenue Service (IRS) Tax Brackets & Tables',
    domain: 'Income Tax',
    startedAt: '2026-08-20 00:00:00 UTC',
    finishedAt: '2026-08-20 00:00:05 UTC',
    status: 'SUCCESS',
    recordsReceived: 28,
    recordsAccepted: 28,
    recordsRejected: 0,
    recordsQuarantined: 0,
    durationMs: 4890,
    correlationId: 'corr-irs-10029-z99'
  }
];

// ============================================================================
// 4. DATA QUARANTINE & TRIAGE QUEUE
// ============================================================================

export const DATA_QUARANTINE_DATA: DataQuarantineItem[] = [
  {
    id: 'quar-2026-001',
    ingestionRunId: 'run-fx-20260824-002',
    sourceName: 'XE Commercial FX Wholesale Feed',
    entityType: 'ExchangeRateObservation',
    rawPayload: '{"base": "USD", "quote": "ARS", "rate": 1845.50, "timestamp": "2026-08-24T17:40:01Z"}',
    reason: 'Anomaly detected: USD/ARS rate shifted by +8.4% compared to 24h rolling median without statutory central bank devaluation alert.',
    severity: 'WARNING',
    status: 'PENDING',
    quarantinedAt: '2026-08-24 17:40:02 UTC'
  },
  {
    id: 'quar-2026-002',
    ingestionRunId: 'run-fx-20260824-002',
    sourceName: 'XE Commercial FX Wholesale Feed',
    entityType: 'ExchangeRateObservation',
    rawPayload: '{"base": "EUR", "quote": "XYZ", "rate": 1.254, "timestamp": "2026-08-24T17:40:01Z"}',
    reason: 'Schema validation failed: "XYZ" is not a recognized ISO 4217 sovereign or special currency identifier.',
    severity: 'ERROR',
    status: 'REJECTED',
    quarantinedAt: '2026-08-24 17:40:02 UTC',
    resolvedBy: 'harry.chama@yamoopay.com',
    resolvedAt: '2026-08-24 17:45:10 UTC',
    resolutionNotes: 'Confirmed fake test symbol returned by upstream provider sandbox mock endpoint. Discarded permanently.'
  },
  {
    id: 'quar-2026-003',
    ingestionRunId: 'run-tax-manual-09',
    sourceName: 'Internal Manual Ingestion',
    entityType: 'TaxBracket',
    rawPayload: '{"jurisdiction": "US-CA", "taxYear": "2026", "lowerBound": 100000, "upperBound": 80000, "rate": 0.093}',
    reason: 'Mathematical boundary invariant violated: lowerBound (100,000) exceeds upperBound (80,000).',
    severity: 'CRITICAL',
    status: 'RESOLVED',
    quarantinedAt: '2026-08-22 11:15:00 UTC',
    resolvedBy: 'lead.tax@yamoopay.com',
    resolvedAt: '2026-08-22 11:30:00 UTC',
    resolutionNotes: 'Corrected bounds transposition error from California FTB schedule draft. Re-ingested cleanly.'
  }
];

// ============================================================================
// 5. DATA QUALITY RULES & SCORECARD (7 DIMENSIONS)
// ============================================================================

export const DATA_QUALITY_RULES_DATA: DataQualityRuleItem[] = [
  {
    id: 'qr-dim-01',
    domain: 'Foreign Exchange',
    name: 'FX Rate Positive Non-Zero Bound',
    description: 'Assert that all imported exchange rates are strictly greater than zero and within sane bounds.',
    dimension: 'VALIDITY',
    severity: 'CRITICAL',
    passThresholdPct: 100.0,
    currentPassRatePct: 100.0,
    recordsCheckedToday: 3840,
    active: true,
    version: '1.0'
  },
  {
    id: 'qr-dim-02',
    domain: 'Foreign Exchange',
    name: 'ISO 4217 Currency Code Referential Integrity',
    description: 'Verify both base and quote currency codes exist in canonical currency repository.',
    dimension: 'REFERENTIAL_INTEGRITY',
    severity: 'CRITICAL',
    passThresholdPct: 100.0,
    currentPassRatePct: 99.98,
    recordsCheckedToday: 3840,
    active: true,
    version: '1.2'
  },
  {
    id: 'qr-dim-03',
    domain: 'Foreign Exchange',
    name: 'Cross-Rate Triangulation Coherence',
    description: 'Assert derived cross-rate A->C matches direct observation within 0.15% maximum spread tolerance.',
    dimension: 'CONSISTENCY',
    severity: 'WARNING',
    passThresholdPct: 99.5,
    currentPassRatePct: 99.82,
    recordsCheckedToday: 1250,
    active: true,
    version: '2.0'
  },
  {
    id: 'qr-dim-04',
    domain: 'Income Tax',
    name: 'Tax Bracket Monotonic Non-Overlapping Curves',
    description: 'Verify upper bound of bracket N equals lower bound of bracket N+1 without gaps or overlaps.',
    dimension: 'CONSISTENCY',
    severity: 'CRITICAL',
    passThresholdPct: 100.0,
    currentPassRatePct: 100.0,
    recordsCheckedToday: 480,
    active: true,
    version: '1.1'
  },
  {
    id: 'qr-dim-05',
    domain: 'Income Tax',
    name: 'Four-Eyes Approval Verification Check',
    description: 'Assert that no TaxRuleSet in ACTIVE status possesses matching createdBy and approvedBy identifiers.',
    dimension: 'ACCURACY',
    severity: 'CRITICAL',
    passThresholdPct: 100.0,
    currentPassRatePct: 100.0,
    recordsCheckedToday: 48,
    active: true,
    version: '1.0'
  },
  {
    id: 'qr-dim-06',
    domain: 'Money Transfers',
    name: 'Fee Transparency Completeness',
    description: 'Ensure transfer quotes declare explicit upfront fee, payment method surcharge, and FX markup.',
    dimension: 'COMPLETENESS',
    severity: 'ERROR',
    passThresholdPct: 99.8,
    currentPassRatePct: 100.0,
    recordsCheckedToday: 2480,
    active: true,
    version: '1.3'
  },
  {
    id: 'qr-dim-07',
    domain: 'Operations',
    name: 'Ingestion Schedule Timeliness SLA',
    description: 'Assert that primary feeds are updated within configured time-to-live thresholds.',
    dimension: 'TIMELINESS',
    severity: 'WARNING',
    passThresholdPct: 99.0,
    currentPassRatePct: 99.65,
    recordsCheckedToday: 180,
    active: true,
    version: '1.0'
  }
];

// ============================================================================
// 6. FRESHNESS ENGINE & SLA POLICIES
// ============================================================================

export const FRESHNESS_POLICIES_DATA: FreshnessPolicyItem[] = [
  {
    domain: 'Foreign Exchange',
    dataType: 'Live Wholesale Mid-Market Rates',
    freshForMinutes: 5,
    warningAfterMinutes: 15,
    staleAfterMinutes: 60,
    criticalAfterMinutes: 120,
    fallbackAllowed: true,
    lastUpdated: '2 minutes ago (17:44 UTC)',
    status: 'FRESH',
    fallbackActive: false,
    lastKnownGoodTimestamp: '2026-08-24 17:44:00 UTC'
  },
  {
    domain: 'Foreign Exchange',
    dataType: 'Central Bank Official Reference Rates',
    freshForMinutes: 1440, // 24 Hours
    warningAfterMinutes: 2160, // 36 Hours
    staleAfterMinutes: 2880, // 48 Hours
    criticalAfterMinutes: 4320, // 72 Hours
    fallbackAllowed: true,
    lastUpdated: '1 hour ago (16:05 UTC)',
    status: 'FRESH',
    fallbackActive: false,
    lastKnownGoodTimestamp: '2026-08-24 16:05:12 UTC'
  },
  {
    domain: 'Money Transfers',
    dataType: 'Remittance Corridor Quotes & Fees',
    freshForMinutes: 10,
    warningAfterMinutes: 30,
    staleAfterMinutes: 120,
    criticalAfterMinutes: 240,
    fallbackAllowed: true,
    lastUpdated: '4 minutes ago (17:42 UTC)',
    status: 'FRESH',
    fallbackActive: false,
    lastKnownGoodTimestamp: '2026-08-24 17:42:10 UTC'
  },
  {
    domain: 'Income Tax & Payroll',
    dataType: 'Statutory 2026 Tax Rulesets & Brackets',
    freshForMinutes: 525600, // 365 Days
    warningAfterMinutes: 518400, // 360 Days
    staleAfterMinutes: 525600,
    criticalAfterMinutes: 540000,
    fallbackAllowed: false,
    lastUpdated: 'Certified 2026.1 Statutory Release',
    status: 'FRESH',
    fallbackActive: false,
    lastKnownGoodTimestamp: '2026-08-20 00:00:00 UTC'
  },
  {
    domain: 'Lending & Mortgage',
    dataType: 'Benchmark Interest Rates (SOFR, EURIBOR)',
    freshForMinutes: 1440,
    warningAfterMinutes: 2880,
    staleAfterMinutes: 4320,
    criticalAfterMinutes: 7200,
    fallbackAllowed: true,
    lastUpdated: '3 hours ago (14:30 UTC)',
    status: 'FRESH',
    fallbackActive: false,
    lastKnownGoodTimestamp: '2026-08-24 14:30:00 UTC'
  }
];

// ============================================================================
// 7. TAX RULESETS (BITEMPORAL & FOUR-EYES SIGNATURES)
// ============================================================================

export const TAX_RULESETS_DATA: TaxRuleSetItem[] = [
  {
    id: 'trs-us-fed-2026-v2',
    jurisdictionId: 'jur-us-fed',
    jurisdictionName: 'United States (Federal)',
    countryCode: 'US',
    taxYear: '2026',
    version: '2026.2-PROD',
    status: 'ACTIVE',
    effectiveFrom: '2026-01-01',
    effectiveTo: '2026-12-31',
    publishedAt: '2025-11-15 14:00:00 UTC',
    verifiedAt: '2025-12-10 09:30:00 UTC',
    sourceName: 'IRS Publication 15-T (Rev. Dec 2025)',
    supersedesVersion: '2026.1-DRAFT',
    makerChecker: {
      createdBy: 'david.miller@yamoopay.com (Data Engineer)',
      reviewedBy: 'sarah.jenkins@yamoopay.com (Tax Data Analyst)',
      approvedBy: 'charlotte.dupont@yamoopay.com (VP Regulatory Compliance)'
    },
    bracketsCount: 7
  },
  {
    id: 'trs-gb-paye-2026-v1',
    jurisdictionId: 'jur-gb-nat',
    jurisdictionName: 'United Kingdom (England & NI)',
    countryCode: 'GB',
    taxYear: '2026/27',
    version: '2026.1-PROD',
    status: 'ACTIVE',
    effectiveFrom: '2026-04-06',
    effectiveTo: '2027-04-05',
    publishedAt: '2026-03-01 10:00:00 UTC',
    verifiedAt: '2026-03-20 16:45:00 UTC',
    sourceName: 'HMRC Statutory Guidance Rates and Thresholds 2026/27',
    makerChecker: {
      createdBy: 'oliver.brown@yamoopay.com (Data Engineer)',
      reviewedBy: 'alistar.mcdonald@yamoopay.com (UK Payroll Specialist)',
      approvedBy: 'charlotte.dupont@yamoopay.com (VP Regulatory Compliance)'
    },
    bracketsCount: 3
  },
  {
    id: 'trs-fr-ir-2026-v1',
    jurisdictionId: 'jur-fr-nat',
    jurisdictionName: 'France (Métropole)',
    countryCode: 'FR',
    taxYear: '2026',
    version: '2026.1-PROD',
    status: 'ACTIVE',
    effectiveFrom: '2026-01-01',
    effectiveTo: '2026-12-31',
    publishedAt: '2025-12-28 12:00:00 UTC',
    verifiedAt: '2026-01-05 11:20:00 UTC',
    sourceName: 'Direction Générale des Finances Publiques (Loi de Finances 2026)',
    makerChecker: {
      createdBy: 'jean.moreau@yamoopay.com (Data Engineer)',
      reviewedBy: 'claire.bernard@yamoopay.com (French Tax Specialist)',
      approvedBy: 'charlotte.dupont@yamoopay.com (VP Regulatory Compliance)'
    },
    bracketsCount: 5
  },
  {
    id: 'trs-ca-fed-2026-v1',
    jurisdictionId: 'jur-ca-fed',
    jurisdictionName: 'Canada (Federal)',
    countryCode: 'CA',
    taxYear: '2026',
    version: '2026.1-PROD',
    status: 'ACTIVE',
    effectiveFrom: '2026-01-01',
    effectiveTo: '2026-12-31',
    publishedAt: '2025-12-05 15:00:00 UTC',
    verifiedAt: '2025-12-18 10:15:00 UTC',
    sourceName: 'Canada Revenue Agency (CRA T4032 Payroll Deductions)',
    makerChecker: {
      createdBy: 'david.miller@yamoopay.com (Data Engineer)',
      reviewedBy: 'lucas.tremblay@yamoopay.com (Canadian Tax Analyst)',
      approvedBy: 'charlotte.dupont@yamoopay.com (VP Regulatory Compliance)'
    },
    bracketsCount: 5
  },
  {
    id: 'trs-us-fed-2027-sched',
    jurisdictionId: 'jur-us-fed',
    jurisdictionName: 'United States (Federal 2027 Advance)',
    countryCode: 'US',
    taxYear: '2027',
    version: '2027.0-SCHEDULED',
    status: 'SCHEDULED',
    effectiveFrom: '2027-01-01',
    effectiveTo: '2027-12-31',
    publishedAt: '2026-08-01 09:00:00 UTC',
    verifiedAt: '2026-08-15 14:00:00 UTC',
    sourceName: 'IRS Projected Inflation Adjustments Rev. Proc. 2026-XX',
    makerChecker: {
      createdBy: 'david.miller@yamoopay.com (Data Engineer)',
      reviewedBy: 'sarah.jenkins@yamoopay.com (Tax Data Analyst)',
      approvedBy: 'charlotte.dupont@yamoopay.com (VP Regulatory Compliance)'
    },
    bracketsCount: 7
  }
];

// ============================================================================
// 8. DATA CONFLICT MANAGEMENT & ARBITRATION
// ============================================================================

export const DATA_CONFLICTS_DATA: DataConflictItem[] = [
  {
    id: 'conf-2026-08-01',
    entityType: 'ExchangeRate',
    entityKey: 'EUR/USD',
    sourceA: {
      name: 'European Central Bank (ECB)',
      value: '1.0845',
      timestamp: '2026-08-24 16:00 UTC',
      authority: 'STATUTORY'
    },
    sourceB: {
      name: 'XE Commercial Wholesale Feed',
      value: '1.0852',
      timestamp: '2026-08-24 16:00 UTC',
      authority: 'AGGREGATED'
    },
    variancePct: 0.064,
    detectedAt: '2026-08-24 16:00:05 UTC',
    severity: 'LOW',
    status: 'AUTO_ARBITRATED',
    resolutionMethod: 'Authority Hierarchy Rule #1: Central Bank statutory reference takes precedence for daily benchmark; commercial feed used for live intraday interpolation.',
    arbitratedValue: '1.0845 (Official Daily) / 1.0852 (Intraday Tick)'
  },
  {
    id: 'conf-2026-08-02',
    entityType: 'ExchangeRate',
    entityKey: 'USD/TRY',
    sourceA: {
      name: 'Central Bank of the Republic of Turkey (CBRT)',
      value: '34.12',
      timestamp: '2026-08-24 15:30 UTC',
      authority: 'STATUTORY'
    },
    sourceB: {
      name: 'Commercial Liquidity Aggregator',
      value: '34.28',
      timestamp: '2026-08-24 15:30 UTC',
      authority: 'AGGREGATED'
    },
    variancePct: 0.468,
    detectedAt: '2026-08-24 15:30:10 UTC',
    severity: 'MEDIUM',
    status: 'RESOLVED',
    resolutionMethod: 'Arbitrated via Mid-Market Volume-Weighted Average with explicit disclaimer regarding emerging market offshore spread.',
    arbitratedValue: '34.18'
  }
];

// ============================================================================
// 9. DATA INCIDENTS & RUNBOOK POST-MORTEMS
// ============================================================================

export const DATA_INCIDENTS_DATA: DataIncidentItem[] = [
  {
    id: 'inc-2026-03',
    incidentNumber: 'INC-84920',
    severity: 'SEV2',
    domain: 'Foreign Exchange',
    title: 'Primary FX Commercial Feed Latency Degradation',
    detectedAt: '2026-08-22 09:14:00 UTC',
    detectedBy: 'Automated Freshness SRE Probe',
    impactedData: 'Live Currency Converter real-time tick streaming',
    impactedMarkets: ['GLOBAL'],
    status: 'RESOLVED',
    rootCause: 'Upstream vendor DNS outage caused 504 gateway timeouts. Circuit breaker automatically activated within 12 seconds.',
    runbookStep: 'RUNBOOK-FX-01: Auto-failover to ECB Reference Rates & XE Secondary Feed. User UI displayed "Using verified fallback rates". Zero calculation failures.',
    resolvedAt: '2026-08-22 09:42:00 UTC'
  },
  {
    id: 'inc-2026-02',
    incidentNumber: 'INC-71940',
    severity: 'SEV3',
    domain: 'Income Tax',
    title: 'Draft French Social Security Ceiling Anomaly in Staging',
    detectedAt: '2026-08-10 14:20:00 UTC',
    detectedBy: 'Staging Promotion Gate Data Quality Suite',
    impactedData: 'France PASS (Plafond Annuel de la Sécurité Sociale) Draft Rule',
    impactedMarkets: ['FR'],
    status: 'RESOLVED',
    rootCause: 'Draft decree contained typo in intermediate monthly tranche bracket. Caught at Staging Gate prior to production promotion.',
    runbookStep: 'RUNBOOK-TAX-03: Quarantine row, alert Tax Lead, rectify JSON schema, verify against Légifrance official journal decree, re-sign Four-Eyes approval.',
    resolvedAt: '2026-08-10 15:10:00 UTC'
  }
];

// ============================================================================
// 10. GLOBAL MARKET READINESS MATRIX (35+ COUNTRIES ACROSS 5 WAVES)
// ============================================================================

export const GLOBAL_MARKET_READINESS_DATA: GlobalMarketReadinessItem[] = [
  // Wave 1: Priority
  {
    countryCode: 'US',
    countryName: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    wave: 1,
    tier: 'Tier 1',
    readinessScore: 100,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'CA',
    countryName: 'Canada',
    flag: '🇨🇦',
    currency: 'CAD',
    wave: 1,
    tier: 'Tier 1',
    readinessScore: 98,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },

  // Wave 2: Europe
  {
    countryCode: 'GB',
    countryName: 'United Kingdom',
    flag: '🇬🇧',
    currency: 'GBP',
    wave: 2,
    tier: 'Tier 1',
    readinessScore: 100,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'FR',
    countryName: 'France',
    flag: '🇫🇷',
    currency: 'EUR',
    wave: 2,
    tier: 'Tier 1',
    readinessScore: 99,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'DE',
    countryName: 'Germany',
    flag: '🇩🇪',
    currency: 'EUR',
    wave: 2,
    tier: 'Tier 1',
    readinessScore: 99,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'CH',
    countryName: 'Switzerland',
    flag: '🇨🇭',
    currency: 'CHF',
    wave: 2,
    tier: 'Tier 1',
    readinessScore: 95,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'ES',
    countryName: 'Spain',
    flag: '🇪🇸',
    currency: 'EUR',
    wave: 2,
    tier: 'Tier 2',
    readinessScore: 92,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'IT',
    countryName: 'Italy',
    flag: '🇮🇹',
    currency: 'EUR',
    wave: 2,
    tier: 'Tier 2',
    readinessScore: 90,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'NL',
    countryName: 'Netherlands',
    flag: '🇳🇱',
    currency: 'EUR',
    wave: 2,
    tier: 'Tier 1',
    readinessScore: 96,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'IE',
    countryName: 'Ireland',
    flag: '🇮🇪',
    currency: 'EUR',
    wave: 2,
    tier: 'Tier 1',
    readinessScore: 94,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },

  // Wave 3: Asia-Pacific
  {
    countryCode: 'AU',
    countryName: 'Australia',
    flag: '🇦🇺',
    currency: 'AUD',
    wave: 3,
    tier: 'Tier 1',
    readinessScore: 97,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'SG',
    countryName: 'Singapore',
    flag: '🇸🇬',
    currency: 'SGD',
    wave: 3,
    tier: 'Tier 1',
    readinessScore: 95,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'IN',
    countryName: 'India',
    flag: '🇮🇳',
    currency: 'INR',
    wave: 3,
    tier: 'Tier 1',
    readinessScore: 94,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'JP',
    countryName: 'Japan',
    flag: '🇯🇵',
    currency: 'JPY',
    wave: 3,
    tier: 'Tier 1',
    readinessScore: 91,
    launchStatus: 'BETA',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'NZ',
    countryName: 'New Zealand',
    flag: '🇳🇿',
    currency: 'NZD',
    wave: 3,
    tier: 'Tier 2',
    readinessScore: 92,
    launchStatus: 'LIVE',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },

  // Wave 4: Middle East & Latin America
  {
    countryCode: 'AE',
    countryName: 'United Arab Emirates',
    flag: '🇦🇪',
    currency: 'AED',
    wave: 4,
    tier: 'Tier 1',
    readinessScore: 88,
    launchStatus: 'BETA',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'SA',
    countryName: 'Saudi Arabia',
    flag: '🇸🇦',
    currency: 'SAR',
    wave: 4,
    tier: 'Tier 2',
    readinessScore: 84,
    launchStatus: 'BETA',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'BR',
    countryName: 'Brazil',
    flag: '🇧🇷',
    currency: 'BRL',
    wave: 4,
    tier: 'Tier 2',
    readinessScore: 82,
    launchStatus: 'BETA',
    taxRulesStatus: 'VERIFICATION_PENDING',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: false, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'MX',
    countryName: 'Mexico',
    flag: '🇲🇽',
    currency: 'MXN',
    wave: 4,
    tier: 'Tier 2',
    readinessScore: 80,
    launchStatus: 'BETA',
    taxRulesStatus: 'VERIFICATION_PENDING',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: false, transfers: true, mortgage: true, vat: true, growth: true }
  },

  // Wave 5: Africa (Universal Global Foundations)
  {
    countryCode: 'NG',
    countryName: 'Nigeria',
    flag: '🇳🇬',
    currency: 'NGN',
    wave: 5,
    tier: 'Tier 2',
    readinessScore: 78,
    launchStatus: 'BETA',
    taxRulesStatus: 'VERIFICATION_PENDING',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: false, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'ZA',
    countryName: 'South Africa',
    flag: '🇿🇦',
    currency: 'ZAR',
    wave: 5,
    tier: 'Tier 2',
    readinessScore: 82,
    launchStatus: 'BETA',
    taxRulesStatus: 'CERTIFIED',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: true, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'KE',
    countryName: 'Kenya',
    flag: '🇰🇪',
    currency: 'KES',
    wave: 5,
    tier: 'Tier 2',
    readinessScore: 76,
    launchStatus: 'BETA',
    taxRulesStatus: 'VERIFICATION_PENDING',
    fxStatus: 'LIVE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: false, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'CM',
    countryName: 'Cameroon',
    flag: '🇨🇲',
    currency: 'XAF',
    wave: 5,
    tier: 'Tier 2',
    readinessScore: 74,
    launchStatus: 'STAGING_READY',
    taxRulesStatus: 'VERIFICATION_PENDING',
    fxStatus: 'REFERENCE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: false, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'SN',
    countryName: 'Senegal',
    flag: '🇸🇳',
    currency: 'XOF',
    wave: 5,
    tier: 'Tier 2',
    readinessScore: 74,
    launchStatus: 'STAGING_READY',
    taxRulesStatus: 'VERIFICATION_PENDING',
    fxStatus: 'REFERENCE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: false, transfers: true, mortgage: true, vat: true, growth: true }
  },
  {
    countryCode: 'MA',
    countryName: 'Morocco',
    flag: '🇲🇦',
    currency: 'MAD',
    wave: 5,
    tier: 'Tier 2',
    readinessScore: 75,
    launchStatus: 'STAGING_READY',
    taxRulesStatus: 'VERIFICATION_PENDING',
    fxStatus: 'REFERENCE_FEED',
    transferQuotesStatus: 'CORRIDOR_ACTIVE',
    toolsSupported: { currency: true, salary: false, transfers: true, mortgage: true, vat: true, growth: true }
  }
];

// ============================================================================
// 11. DATA LINEAGE TRACE SAMPLE (FROM RAW JSON TO SERVING CARD)
// ============================================================================

export const SAMPLE_DATA_LINEAGE_NODES: DataLineageNode[] = [
  {
    step: 'Step 01',
    level: 'SOURCE',
    title: 'Upstream Ingestion Source',
    description: 'European Central Bank (ECB) Reference Rates REST API',
    sourceRef: 'https://data-api.ecb.europa.eu/service/data/EXR/D.USD.EUR.SP00.A',
    timestamp: '2026-08-24 16:00:02 UTC',
    status: 'VERIFIED',
    metadata: {
      provider: 'European Central Bank',
      authority: 'STATUTORY',
      httpStatus: '200 OK',
      sha256: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855'
    }
  },
  {
    step: 'Step 02',
    level: 'RAW',
    title: 'Raw Data Record Storage',
    description: 'Immutable JSON payload stored in `ingestion.raw_records`',
    sourceRef: 'raw_record_id: raw-88392-f01',
    timestamp: '2026-08-24 16:00:03 UTC',
    status: 'VERIFIED',
    metadata: {
      table: 'ingestion.raw_records',
      ingestionRunId: 'run-fx-20260824-001',
      payloadSize: '4.2 KB',
      retentionPolicy: '30 Days Raw Hot Archive'
    }
  },
  {
    step: 'Step 03',
    level: 'STAGING',
    title: 'Staging Validation & Quality Gate',
    description: 'Executed 7 Data Quality assertions (Completeness, Bounds, Non-Zero, Invariants)',
    sourceRef: 'check_id: qc-ecb-20260824-pass',
    timestamp: '2026-08-24 16:00:04 UTC',
    status: 'CHECKED',
    metadata: {
      assertionsPassed: '7 of 7 (100%)',
      anomalyScore: '0.000',
      quarantineTriggered: 'false'
    }
  },
  {
    step: 'Step 04',
    level: 'CANONICAL',
    title: 'PostgreSQL System of Record',
    description: 'Committed to `exchange.rate_observations` with exact NUMERIC(18,6)',
    sourceRef: 'observation_id: obs-eurusd-20260824-01',
    timestamp: '2026-08-24 16:00:05 UTC',
    status: 'VERIFIED',
    metadata: {
      table: 'exchange.rate_observations',
      baseCurrency: 'EUR (UUID: c01a-eur)',
      quoteCurrency: 'USD (UUID: c02b-usd)',
      rate: '1.084500 NUMERIC(18,6)',
      rateType: 'REFERENCE (Statutory Benchmark)'
    }
  },
  {
    step: 'Step 05',
    level: 'DERIVED',
    title: 'Derived Reciprocal & Cross-Rate Engine',
    description: 'Deterministic computation of USD/EUR reciprocal (0.922084) & cross pairs',
    sourceRef: 'formula: 1 / 1.084500',
    timestamp: '2026-08-24 16:00:06 UTC',
    status: 'TRANSFORMED',
    metadata: {
      reciprocalCalculated: '0.922084 EUR/USD',
      roundingPolicy: 'HALF_EVEN (Banker\'s Rounding)',
      derivedFlag: 'is_derived = true'
    }
  },
  {
    step: 'Step 06',
    level: 'SERVING',
    title: 'Client UI Serving & Freshness Badge',
    description: 'Materialized view served to Currency Converter & Trust Lineage Drawer',
    sourceRef: 'UI Component: <FinancialResultCard /> & <DataFreshnessBadge />',
    timestamp: '2026-08-24 17:45:00 UTC (Current)',
    status: 'CACHED',
    metadata: {
      freshnessStatus: 'FRESH (Age: 105 mins < 1440m TTL)',
      trustBadge: 'ECB 2026 Certified Benchmark',
      userTraceable: 'true'
    }
  }
];

// ============================================================================
// 12. CONCRETE POSTGRESQL 18 DDL / SCHEMA DEFINITIONS
// ============================================================================

export const POSTGRES_SCHEMAS_P3 = [
  {
    schema: 'geography',
    name: 'geography.countries',
    description: 'Universal sovereign nation reference table complying with ISO 3166-1 standard.',
    ddl: `CREATE TABLE geography.countries (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  iso_alpha2 VARCHAR(2) NOT NULL UNIQUE,
  iso_alpha3 VARCHAR(3) NOT NULL UNIQUE,
  iso_numeric VARCHAR(3) NOT NULL UNIQUE,
  canonical_name VARCHAR(128) NOT NULL,
  official_name VARCHAR(255) NOT NULL,
  status VARCHAR(32) NOT NULL DEFAULT 'ACTIVE' CHECK (status IN ('ACTIVE', 'SUSPENDED', 'HISTORICAL')),
  default_currency_id UUID NOT NULL,
  default_locale VARCHAR(16) NOT NULL DEFAULT 'en-US',
  calling_code VARCHAR(16),
  timezone_iana VARCHAR(64) NOT NULL DEFAULT 'UTC',
  enabled BOOLEAN NOT NULL DEFAULT true,
  supported_by_yamoopay BOOLEAN NOT NULL DEFAULT false,
  launch_wave INT NOT NULL DEFAULT 5 CHECK (launch_wave BETWEEN 1 AND 5),
  launched_at TIMESTAMPTZ,
  created_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP,
  updated_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE INDEX idx_countries_iso2 ON geography.countries(iso_alpha2);
CREATE INDEX idx_countries_supported ON geography.countries(supported_by_yamoopay, launch_wave);`
  },
  {
    schema: 'currency',
    name: 'currency.currencies',
    description: 'ISO 4217 international fiat assets, minor unit decimals, and precision specifications.',
    ddl: `CREATE TABLE currency.currencies (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  code VARCHAR(3) NOT NULL UNIQUE, -- ISO 4217 (e.g. USD, EUR, JPY)
  numeric_code VARCHAR(3) NOT NULL UNIQUE,
  canonical_name VARCHAR(128) NOT NULL,
  symbol VARCHAR(8) NOT NULL,
  minor_unit INT NOT NULL DEFAULT 2 CHECK (minor_unit BETWEEN 0 AND 4),
  currency_type VARCHAR(32) NOT NULL DEFAULT 'FIAT' CHECK (currency_type IN ('FIAT', 'SPECIAL', 'COMMODITY_REF')),
  status VARCHAR(32) NOT NULL DEFAULT 'ACTIVE' CHECK (status IN ('ACTIVE', 'HISTORICAL', 'SUSPENDED')),
  valid_from DATE NOT NULL DEFAULT '1970-01-01',
  valid_to DATE,
  created_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP,
  updated_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE INDEX idx_currencies_code ON currency.currencies(code);`
  },
  {
    schema: 'exchange',
    name: 'exchange.rate_observations',
    description: 'Partitioned observational foreign exchange rates with source provenance and confidence scores.',
    ddl: `CREATE TABLE exchange.rate_observations (
  id UUID DEFAULT gen_random_uuid(),
  base_currency_id UUID NOT NULL REFERENCES currency.currencies(id),
  quote_currency_id UUID NOT NULL REFERENCES currency.currencies(id),
  rate NUMERIC(18, 6) NOT NULL CHECK (rate > 0),
  rate_type VARCHAR(32) NOT NULL DEFAULT 'MID_MARKET' CHECK (rate_type IN ('MID_MARKET', 'BID', 'ASK', 'REFERENCE', 'PROVIDER', 'INDICATIVE')),
  is_derived BOOLEAN NOT NULL DEFAULT false,
  is_reciprocal BOOLEAN NOT NULL DEFAULT false,
  derivation_path TEXT,
  source_id UUID NOT NULL REFERENCES ingestion.data_sources(id),
  ingestion_run_id UUID NOT NULL REFERENCES ingestion.runs(id),
  observed_at TIMESTAMPTZ NOT NULL,
  received_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP,
  valid_at TIMESTAMPTZ NOT NULL,
  confidence_score NUMERIC(5, 2) NOT NULL DEFAULT 100.00 CHECK (confidence_score BETWEEN 0 AND 100),
  quality_status VARCHAR(32) NOT NULL DEFAULT 'VALIDATED' CHECK (quality_status IN ('VALIDATED', 'WARNING', 'QUARANTINED')),
  PRIMARY KEY (id, observed_at)
) PARTITION BY RANGE (observed_at);

-- Monthly partitions
CREATE TABLE exchange.rate_observations_y2026m08 PARTITION OF exchange.rate_observations
  FOR VALUES FROM ('2026-08-01 00:00:00+00') TO ('2026-09-01 00:00:00+00');

CREATE INDEX idx_rate_obs_pair_time ON exchange.rate_observations (base_currency_id, quote_currency_id, observed_at DESC);`
  },
  {
    schema: 'tax',
    name: 'tax.rulesets',
    description: 'Bitemporal statutory tax year rulesets with Four-Eyes Maker/Checker audit signatures.',
    ddl: `CREATE TABLE tax.rulesets (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  jurisdiction_id UUID NOT NULL REFERENCES geography.jurisdictions(id),
  tax_year VARCHAR(16) NOT NULL, -- e.g. '2026', '2026/27'
  version VARCHAR(32) NOT NULL, -- e.g. '2026.1-PROD'
  status VARCHAR(32) NOT NULL DEFAULT 'DRAFT' CHECK (status IN ('DRAFT', 'REVIEW', 'VERIFIED', 'SCHEDULED', 'ACTIVE', 'SUPERSEDED', 'ARCHIVED')),
  effective_from DATE NOT NULL,
  effective_to DATE NOT NULL,
  published_at TIMESTAMPTZ NOT NULL,
  verified_at TIMESTAMPTZ,
  source_id UUID NOT NULL REFERENCES ingestion.data_sources(id),
  supersedes_id UUID REFERENCES tax.rulesets(id),
  created_by VARCHAR(128) NOT NULL,
  reviewed_by VARCHAR(128),
  approved_by VARCHAR(128),
  created_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP,
  updated_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP,
  CONSTRAINT chk_four_eyes_integrity CHECK (
    status != 'ACTIVE' OR (approved_by IS NOT NULL AND approved_by != created_by)
  )
);

CREATE INDEX idx_tax_rules_active ON tax.rulesets (jurisdiction_id, tax_year) WHERE status = 'ACTIVE';`
  },
  {
    schema: 'tax',
    name: 'tax.brackets',
    description: 'Ordered, progressive income tax brackets with non-float NUMERIC currency bounds.',
    ddl: `CREATE TABLE tax.brackets (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  ruleset_id UUID NOT NULL REFERENCES tax.rulesets(id) ON DELETE CASCADE,
  bracket_index INT NOT NULL,
  filing_status VARCHAR(32) NOT NULL DEFAULT 'SINGLE' CHECK (filing_status IN ('SINGLE', 'MARRIED_JOINT', 'MARRIED_SEPARATE', 'HEAD_OF_HOUSEHOLD', 'ALL')),
  lower_bound NUMERIC(18, 2) NOT NULL CHECK (lower_bound >= 0),
  upper_bound NUMERIC(18, 2), -- NULL signifies infinity
  marginal_rate NUMERIC(8, 6) NOT NULL CHECK (marginal_rate BETWEEN 0 AND 1.0),
  fixed_base_amount NUMERIC(18, 2) NOT NULL DEFAULT 0.00 CHECK (fixed_base_amount >= 0),
  calculation_method VARCHAR(32) NOT NULL DEFAULT 'PROGRESSIVE' CHECK (calculation_method IN ('PROGRESSIVE', 'FLAT', 'REGRESSIVE')),
  created_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP,
  CONSTRAINT chk_bracket_bounds CHECK (upper_bound IS NULL OR upper_bound >= lower_bound),
  UNIQUE (ruleset_id, filing_status, bracket_index)
);

CREATE INDEX idx_tax_brackets_ruleset ON tax.brackets (ruleset_id, filing_status, bracket_index);`
  },
  {
    schema: 'transfer',
    name: 'transfer.quotes',
    description: 'Append-only historical and real-time remittance quotes with explicit fee breakdowns.',
    ddl: `CREATE TABLE transfer.quotes (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  provider_id UUID NOT NULL REFERENCES transfer.providers(id),
  corridor_id UUID NOT NULL REFERENCES transfer.corridors(id),
  send_amount NUMERIC(18, 2) NOT NULL CHECK (send_amount > 0),
  send_currency_id UUID NOT NULL REFERENCES currency.currencies(id),
  upfront_fee NUMERIC(18, 2) NOT NULL DEFAULT 0.00 CHECK (upfront_fee >= 0),
  exchange_rate NUMERIC(18, 6) NOT NULL CHECK (exchange_rate > 0),
  recipient_amount NUMERIC(18, 2) NOT NULL CHECK (recipient_amount > 0),
  receive_currency_id UUID NOT NULL REFERENCES currency.currencies(id),
  fx_markup_pct NUMERIC(6, 4) NOT NULL DEFAULT 0.0000,
  payment_method VARCHAR(64) NOT NULL,
  delivery_method VARCHAR(64) NOT NULL,
  estimated_delivery_minutes INT,
  quoted_at TIMESTAMPTZ NOT NULL,
  expires_at TIMESTAMPTZ NOT NULL,
  source_id UUID NOT NULL REFERENCES ingestion.data_sources(id),
  created_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP
);

CREATE INDEX idx_transfer_quotes_corridor ON transfer.quotes (corridor_id, quoted_at DESC);`
  },
  {
    schema: 'ingestion',
    name: 'ingestion.quarantine',
    description: 'Isolated holding queue for corrupted, statistically anomalous, or invalid external payloads.',
    ddl: `CREATE TABLE ingestion.quarantine (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  ingestion_run_id UUID NOT NULL REFERENCES ingestion.runs(id),
  source_id UUID NOT NULL REFERENCES ingestion.data_sources(id),
  entity_type VARCHAR(64) NOT NULL,
  raw_payload JSONB NOT NULL,
  reason TEXT NOT NULL,
  severity VARCHAR(16) NOT NULL DEFAULT 'WARNING' CHECK (severity IN ('INFO', 'WARNING', 'ERROR', 'CRITICAL')),
  status VARCHAR(32) NOT NULL DEFAULT 'PENDING' CHECK (status IN ('PENDING', 'RESOLVED', 'REJECTED', 'DISCARDED')),
  quarantined_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP,
  resolved_by VARCHAR(128),
  resolved_at TIMESTAMPTZ,
  resolution_notes TEXT
);

CREATE INDEX idx_quarantine_pending ON ingestion.quarantine (status, quarantined_at DESC) WHERE status = 'PENDING';`
  },
  {
    schema: 'audit',
    name: 'audit.logs',
    description: 'Immutable append-only ledger for all administrative mutations, ruleset activations, and overrides.',
    ddl: `CREATE TABLE audit.logs (
  id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
  actor_id VARCHAR(128) NOT NULL,
  actor_role VARCHAR(64) NOT NULL,
  action VARCHAR(64) NOT NULL, -- e.g. 'RULESET_ACTIVATED', 'OVERRIDE_APPLIED'
  entity_type VARCHAR(64) NOT NULL,
  entity_id VARCHAR(128) NOT NULL,
  before_state JSONB,
  after_state JSONB,
  reason TEXT,
  correlation_id VARCHAR(64) NOT NULL,
  ip_address INET,
  created_at TIMESTAMPTZ NOT NULL DEFAULT CURRENT_TIMESTAMP
);

-- Revoke mutation rights from standard application users
REVOKE UPDATE, DELETE ON audit.logs FROM PUBLIC;
CREATE INDEX idx_audit_entity ON audit.logs (entity_type, entity_id, created_at DESC);
CREATE INDEX idx_audit_corr ON audit.logs (correlation_id);`
  }
];
